import { Form, Input, Button, Row, Col, Card, Checkbox } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SECTIONS_ALL_KEYS } from 'constants/permissions';
import {
  createRole,
  fetchPermissionsByRoleName,
  fetchRoleById,
  fetchRolesGroup,
  updateRole
} from 'services/api/roles';
import { PermissionRow, RoleListRow } from 'types/api/roles';

import useOrgEffect from 'hooks/useOrgEffect';
import { Loading, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { RolesGroupSelect } from '../components';
import { checkPermission } from 'utils/permission';

const RoleDetails: FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [parentId, setParentId] = useState('');
  const [isLoading, setLoading] = useState(!!id);
  const [values, setValues] = useState<Record<string, number>>({});

  const [roleGroups, setRoleGroups] = useState<RoleListRow[]>([]);
  const [roleGroupsLoading, setRoleGroupsLoading] = useState(true);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setRoleGroupsLoading(true);
        const rows = await fetchRolesGroup();
        if (!cancelled) setRoleGroups(rows);
      } catch {
        // swallow — empty group options on failure
      } finally {
        if (!cancelled) setRoleGroupsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const parent = roleGroups.find(item => String(item.id) === String(parentId));

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const role = await fetchRoleById(id);
        if (cancelled) return;

        if (role?.parent_id != null) {
          setParentId(String(role.parent_id));
          form.setFieldsValue({
            name: role.name,
            parent_id: String(role.parent_id)
          });
          const permissionValues = (role.role_permissions ?? []).reduce<
            Record<string, number>
          >((acc, val) => {
            const pid = val?.permission_id;
            if (pid == null) return acc;
            const current = acc[pid] || 0;

            return { ...acc, [pid]: current + val.value };
          }, {});

          setValues(permissionValues);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, form]);

  useEffect(() => {
    if (!parent?.name) return;
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchPermissionsByRoleName(parent.name);
        if (!cancelled) setPermissions(rows);
      } catch {
        // swallow — empty checkbox grid on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [parent?.name]);

  const onFinish = useCallback(
    async (data: Record<string, string | number>) => {
      const input = {
        name: data.name as string,
        parent_id: data.parent_id != null ? Number(data.parent_id) : undefined,
        values
      };

      try {
        setIsSaving(true);

        if (id) {
          await updateRole(id, input);
        } else {
          await createRole(input);
        }

        navigate('/access/roles');
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    [id, values, navigate]
  );

  const onChangeRoleGroup = (id: string) => {
    setParentId(id);
    setValues({});
  };

  const onChangePermission = (
    permission: PermissionRow,
    checked: boolean,
    value: number
  ) => {
    const { id } = permission;

    setValues(prev => {
      const current = prev[id] || 0;
      const result = checked ? current + value : current - value;

      if (!result) {
        delete prev[id];

        return { ...prev };
      }

      return {
        ...prev,
        [id]: result
      };
    });
  };

  if (isLoading || roleGroupsLoading) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      title={id ? 'Edit role' : 'Create Role'}
      onClickBack={() => navigate('/access/roles')}
      extra={[
        <Button
          type="primary"
          key="create-role"
          loading={isSaving}
          onClick={() => form.submit()}
        >
          {id ? 'Edit' : 'Create'}
        </Button>
      ]}
    >
      <Form form={form} onFinish={onFinish} layout="vertical" size="large">
        <Row gutter={16}>
          <Col sm={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col sm={12}>
            <Form.Item
              name="parent_id"
              label="Role group"
              rules={[{ required: true }]}
            >
              <RolesGroupSelect value={parentId} onChange={onChangeRoleGroup} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {!!parent && (
        <Row gutter={[16, 16]} align="stretch">
          {permissions.map(permission => (
            <Col span={8} key={`permission-${permission.id}`}>
              <Card
                title={t(`permissions.${SECTIONS_ALL_KEYS[permission.name]}`)}
                headStyle={{ fontSize: 18 }}
                style={{ height: '100%' }}
              >
                <Row gutter={[0, 12]}>
                  {permission.values.map(value => {
                    const checked = checkPermission(
                      value.value,
                      values[permission.id]
                    );

                    return (
                      <Col span={24} key={`${permission.id}-value-${value.id}`}>
                        <Checkbox
                          checked={checked}
                          onChange={e =>
                            onChangePermission(
                              permission,
                              e.target.checked,
                              value.value
                            )
                          }
                        >
                          {t(`permissions.${value.name}`)}
                        </Checkbox>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </PageWrapper>
  );
};

export default RoleDetails;
